import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { useCallback, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  Box,
  Button,
  CustomSelect,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  SectionHeader,
  TableActionTypes,
  TableState,
  TABLE_INITIAL_STATE,
  useDataTable,
  useSnackbarHandler,
  MUIDataTableColumn
} from 'web-library';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import Icon, { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useDeleteOnlineTutorials } from '../../../api-refactored/onlineTutorials/useDeleteOnlineTutorials';
import { ExamLevelsAndResourceTypesResponse } from '../../../api-refactored/onlineTutorials/useGetExamLevelsAndResourceTypes';
import {
  OnlineTutorialsListingContent,
  usePostOnlineTutorials,
} from '../../../api-refactored/onlineTutorials/usePostOnlineTutorials';
import { KeyValueResponse } from '../../../api-refactored/typings';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import { ApiPathParam } from '../../../models/SearchParamSchema';
import RouteKey from '../../../routes/key';
import { existInArray, getIndexValue } from '../../../utils/ArrayUtils';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import { formAPIPageable, parseLocationSearch } from '../../../utils/StringUtils';
import DeleteResourceDialog from './DeleteResourceDialog';

const { addResource, editResource } = RouteKey['useful-information'].child;

const useStyles = makeStyles()((theme) =>
({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  addButtonContainer: {
    margin: theme.spacing(1),
    display: 'inline-flex',
    cursor: 'pointer',
  },
  button: {
    margin: 'auto',
    padding: 0,
    minWidth: 0,
  },
  icon: {
    margin: 'auto',
    marginRight: theme.spacing(0.5),
  },
  buttonContainer: {
    display: 'inline-flex',
    cursor: 'pointer',
  },
  buttonRemove: {
    color: theme.color.state.stateError,
  },
  buttonFont: {
    '& span': {
      fontSize: '18px !important',
    },
  },
  iconAction: {
    marginRight: theme.spacing(1),
  },
  containerTable: {
    marginBottom: theme.spacing(7),
  },
  select: {
    marginBottom: theme.spacing(3),
  },
}));

const menuItemsData = (data: KeyValueResponse[] | undefined) =>
  data?.map((value) => ({
    children: value.description,
    key: value.code,
    value: value.code,
  })) || [];

interface TableColumnProps {
  classes: Record<string,string>;
  t: TFunction;
  theme: AugmentedTheme;
  filterData: ExamLevelsAndResourceTypesResponse | undefined;
  data: OnlineTutorialsListingContent[];
  deleteClick: (data: OnlineTutorialsListingContent) => () => void;
  editClick: (data: OnlineTutorialsListingContent) => () => void;
  hasEditPermission: boolean;
  hasDeletePermission: boolean;
}

const tableColumns = ({
  classes,
  t,
  theme,
  filterData,
  data,
  deleteClick,
  editClick,
  hasDeletePermission,
  hasEditPermission,
}: TableColumnProps): MUIDataTableColumnDef[] => {
  const resourceTypeData = menuItemsData(filterData?.resourceTypes);
  return [
    {
      name: 'title',
      label: t('usefulInformation->>Listing.Title'),
      options: {
        setCellProps: () => ({ style: { width: '23%' } }),
        filter: false,
        sort: false,
      },
    },
    {
      name: 'examLevel',
      label: t('usefulInformation->>Listing.ExamLevel'),
      options: {
        setCellProps: () => ({ style: { width: '12%' } }),
        sort: false,
        filterType: 'custom',
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              // eslint-disable-next-line react/destructuring-assignment
              value={filterList[index][0]}
              displayEmpty
              menuItems={menuItemsData(filterData?.examLevels)}
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event.target.value as string;
                onChange(filterList[index], index, column);
              }}
              label={t('usefulInformation->>Listing.ExamLevel')}
              labelId="input-select-exam-level"
              id="input-select-exam-level"
            />
          ),
        },
      },
    },
    {
      name: 'resourceType',
      label: t('usefulInformation->>Listing.ResourceType'),
      options: {
        setCellProps: () => ({ style: { width: '23%' } }),
        sort: false,
        filterType: 'custom',
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              // eslint-disable-next-line react/destructuring-assignment
              value={filterList[index][0]}
              displayEmpty
              menuItems={resourceTypeData}
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event.target.value as string;

                onChange(filterList[index], index, column);
              }}
              label={t('usefulInformation->>Listing.ResourceType')}
              labelId="input-select-resource-type"
              id="input-select-resource-type"
            />
          ),
        },
      },
    },
    {
      name: 'updatedAt',
      label: t('usefulInformation->>Listing.UpdatedDateTime'),
      options: {
        setCellProps: () => ({ style: { width: '23%' } }),
        filter: false,
        customBodyRenderLite: (index: number) =>
          data[index] && data[index].updatedAt
            ? stringToLongDateAndShortTime(data[index].updatedAt)
            : '-',
      },
    },
    {
      name: 'action',
      label: t('usefulInformation->>Listing.Actions'),
      options: {
        setCellProps: () => ({ style: { width: '19%' } }),
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Grid container spacing={8}>
              {hasEditPermission && (
                <Grid className={classes.buttonContainer}>
                  <Button
                    id="button-resource-edit"
                    className={`${classes.button} ${classes.buttonFont}`}
                    onClick={editClick(data[dataIndex])}
                    variant="text"
                    color="primary"
                    size="small"
                  >
                    <Icon
                      size="M"
                      name={IconName.edit}
                      color={theme.color.icon.iconSelected}
                      className={classes.iconAction}
                    />
                    {t('usefulInformation->>Listing.Edit')}
                  </Button>
                </Grid>
              )}
              {hasDeletePermission && (
                <Grid className={classes.buttonContainer}>
                  <Button
                    id="button-resource-delete"
                    className={`${classes.button} ${classes.buttonFont} ${classes.buttonRemove}`}
                    onClick={deleteClick(data[dataIndex])}
                    variant="text"
                    size="small"
                  >
                    <Icon
                      size="M"
                      name={IconName.trash}
                      color={theme.color.icon.iconTrash}
                      className={classes.iconAction}
                    />
                    {t('usefulInformation->>Listing.Delete')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </>
        ),
      },
    },
  ];
};

const appendedServerSizePaginationColumnOption: MUIDataTableOptions = {
  filter: true,
  filterType: 'multiselect',
  pagination: true,
  sort: true,
  selectToolbarPlacement: 'none',
  sortOrder: {
    name: 'updatedAt',
    direction: 'desc',
  },
};

interface SectionHeaderTableProps {
  classes: Record<string,string>;
  t: TFunction;
  theme: AugmentedTheme;
  onClick: () => void;
  hasCreatePermission: boolean;
}

const SectionHeaderTable = ({
  classes,
  t,
  theme,
  onClick,
  hasCreatePermission,
}: SectionHeaderTableProps) => (
  <Grid container direction="row" className={classes.headerContainer}>
    <SectionHeader title={t('usefulInformation->>Listing.ResourceList')} />
    {hasCreatePermission && (
      <Box className={classes.addButtonContainer}>
        <Button id="button-add-resource" onClick={onClick} size="medium">
          <Icon
            size="M"
            className={classes.icon}
            name={IconName.add}
            color={theme.color.icon.iconInverse}
          />
          {t('usefulInformation->>Listing.AddResource')}
        </Button>
      </Box>
    )}
  </Grid>
);

export interface ResourceRequestParam extends ApiPathParam {
  search: string;
  examLevels: string[];
  resourceTypes: string[];
}

const getFiltersMap = (examLevel: string | undefined, resourceType: string | undefined) => {
  const filterMap: {
    [key: string]: any;
  } = {};

  if (examLevel) {
    filterMap.examLevel = examLevel.replace('[', '').replace(']', '').split(',');
  }

  if (resourceType) {
    filterMap.resourceType = resourceType.replace('[', '').replace(']', '').split(',');
  }

  return filterMap;
};

const useGetRequestParam = () => {
  const location = useLocation<ResourceRequestParam>();
  const requestParam = parseLocationSearch(location.search);
  const filterMap = getFiltersMap(requestParam.examLevel, requestParam.resourceType);
  const sortColumn = requestParam.sort?.split(',')[0];
  const order = requestParam.sort?.split(',')[1] === 'asc' ? 'asc' : 'desc';

  return {
    filterMap,
    page: requestParam.page,
    size: requestParam.size,
    search: '',
    sort: sortColumn,
    order,
  };
};

const formSearchQuery = (state: TableState) => {
  let path = formAPIPageable({
    order: state.order,
    sort: state.sortColumn,
    page: state.page,
    pageSize: state.size,
  });
  if (path !== '?') {
    path += '&';
  }

  if (getIndexValue(state.filters.examLevels, 0)) {
    path += `examLevel=${getIndexValue(state.filters.examLevels, 0)}&`;
  }

  if (getIndexValue(state.filters.resourceTypes, 0)) {
    path += `resourceType=${getIndexValue(state.filters.resourceTypes, 0)}&`;
  }

  if (path.length) {
    path = path.substring(0, path.length - 1);
  }

  return path;
};

interface ResourceListSearchAndFilterProps {
  filterData: ExamLevelsAndResourceTypesResponse | undefined;
}

const ResourceListSearchAndFilter = ({ filterData }: ResourceListSearchAndFilterProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const requestParam = useGetRequestParam();
  const { path } = useRouteMatch();

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: requestParam.sort || 'updatedAt',
    order: requestParam.order === 'asc' ? 'asc' : 'desc',
    size: requestParam.size !== undefined ? parseInt(requestParam.size, 10) : 10,
    page: requestParam.page !== undefined ? parseInt(requestParam.page, 10) : 0,
    search: requestParam.search || '',
    filters: requestParam.filterMap,
  });

  const [modalState, setModalState] = useState<boolean>(false);
  const [dataSelected, setDataSelected] = useState<OnlineTutorialsListingContent>();

  const { mutate, isLoading } = usePostOnlineTutorials({
    onSuccess: (data) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.content,
            count: data.totalElements,
          },
        } as TableActionTypes);

        history.replace(`${location.pathname}${formSearchQuery(state)}`);
      }
    },
  });

  const examLevelFilter =
    state.filters.examLevel && state.filters.examLevel[0]
      ? filterData?.examLevels.find((v) => v.code === state.filters.examLevel[0])
      : '';
  const resourceTypeFilter =
    state.filters.resourceType && state.filters.resourceType[0]
      ? filterData?.resourceTypes.find((v) => v.code === state.filters.resourceType[0])
      : '';

  const getData = useCallback(() => {
    mutate({
      examLevels: examLevelFilter ? [examLevelFilter] : [],
      resourceTypes:
        resourceTypeFilter && resourceTypeFilter.code !== 'ALL' ? [resourceTypeFilter] : [],
      apiParams: {
        pageSize: state.size,
        page: state.page,
        order: state.order,
        sort: state.sortColumn,
      },
    });
  }, [
    examLevelFilter,
    mutate,
    resourceTypeFilter,
    state.order,
    state.page,
    state.size,
    state.sortColumn,
  ]);

  const [deleteStatus, setDeleteStatus] = useState<MutationStatus>('idle');

  const { mutate: deleteMutate } = useDeleteOnlineTutorials({
    onSuccess: () => {
      setDeleteStatus('success');
      setModalState(false);

      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('usefulInformation->>ResourceForm.ResourceSuccessfullyDeleted'),
      });
      getData();
    },
    onError: () => {
      setDeleteStatus('error');
    },
  });

  const userAuthorities = useGetUserAuthorities();

  const hasCreatePermission = existInArray(
    userAuthorities.data,
    AUTHORITY_NAMES.CP_ADMIN_USEFUL_INFORMATION_CREATE
  );
  const hasEditPermission = existInArray(
    userAuthorities.data,
    AUTHORITY_NAMES.CP_ADMIN_USEFUL_INFORMATION_EDIT
  );
  const hasDeletePermission = existInArray(
    userAuthorities.data,
    AUTHORITY_NAMES.CP_ADMIN_USEFUL_INFORMATION_DELETE
  );

  useEffect(() => {
    getData();
  }, [getData]);

  const addClick = () => {
    history.push(`${path}/${addResource.route}`);
  };

  const editClick = (data: OnlineTutorialsListingContent) => () => {
    history.push(`${path}/${editResource.route}/${data.uuid}`);
  };

  const deleteClick = (data: OnlineTutorialsListingContent) => () => {
    setDeleteStatus('idle');
    setDataSelected(data);
    setModalState(true);
  };

  const onDeleteClick = () => {
    setDeleteStatus('loading');
    deleteMutate(dataSelected?.uuid || '');
  };

  return (
    <>
      <SectionHeaderTable
        classes={classes}
        onClick={addClick}
        t={t}
        theme={theme}
        hasCreatePermission={hasCreatePermission}
      />
      <Box className={classes.containerTable}>
        <MUIDataTable
          state={state}
          isLoading={isLoading}
          dispatch={tableDispatch}
          columns={tableColumns({
            classes,
            t,
            theme,
            filterData,
            data: state.data,
            editClick,
            deleteClick,
            hasEditPermission,
            hasDeletePermission,
          })}
          data={state.data}
          options={{ ...tOptions, ...appendedServerSizePaginationColumnOption }}
          rowsPerPageOptions={[10, 20, 30, 40, 50]}
          emptyDataLabel={t('usefulInformation->>Listing.EmptyUsefulInformation')}
        />
      </Box>
      <DeleteResourceDialog
        modalState={modalState}
        deleteClick={onDeleteClick}
        onDialogClose={() => setModalState(false)}
        resourceTitle={dataSelected?.title || ''}
        deleteStatus={deleteStatus}
      />
    </>
  );
};

export default ResourceListSearchAndFilter;
